import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PeoplePickerComponent, IPeopleDetails } from '../../../core/components/people-picker/people-picker.component';
import { ReviewerService } from '../../../core/services/reviewer.service';
import { UserRoleService } from 'src/app/core/services/user-role.service';
import { DateFormatService } from '../../../core/services/date-format.service';

import { AddIssueForm, ActionPlanRow, ActionPlanForm, RiskCategoryRow, ExistingFile, createDefaultAddIssueForm, createDefaultActionPlanForm } from './create-new-issue.interfaces';
import {
  LOCATION_OPTIONS, SERVICE_LINE_OPTIONS,
  ISSUE_RATING_OPTIONS, ACTION_REQUIRED_BY_OPTIONS,
  REP_LETTER_CONFLICT_OPTIONS, FINANCIAL_YEAR_OPTIONS,
  QUARTER_OPTIONS, REPORT_STATUS_OPTIONS,
  ALLOWED_FILE_EXTENSIONS,
  MAX_FILE_SIZE_BYTES, REP_LETTER_QUESTIONS_OPTIONS,
  ACTION_PLAN_STATUS_OPTIONS, ISSUE_FROM_GO_LIVE_OPTIONS,
} from './create-new-issue.constants';

export { AddIssueForm, ActionPlanRow };

@Component({
  selector: 'app-create-new-issue',
  standalone: true,
  imports: [CommonModule, FormsModule, PeoplePickerComponent],
  templateUrl: './create-new-issue.component.html',
  styleUrl: './create-new-issue.component.css',
})
export class CreateNewIssueComponent implements OnChanges, OnDestroy {
  private reviewerService = inject(ReviewerService);
  private router = inject(Router);
  private userRoleService = inject(UserRoleService);
  private dateFormatSvc = inject(DateFormatService);
  private _activePicker: HTMLInputElement | null = null;
  private _pickerResizeHandler: (() => void) | null = null;

  @ViewChild('subscriberPickerRef') subscriberPickerRef?: PeoplePickerComponent;
  @ViewChildren('ownerPickerRef') ownerPickerRefs!: QueryList<PeoplePickerComponent>;

  get isManagerRole(): boolean {
    return this.userRoleService.isManager;
  }

  get isReviewerRole(): boolean {
    return this.userRoleService.isReviewer;
  }

  get isSupervisorRole(): boolean {
    return this.userRoleService.isSupervisor;
  }

  @Input() isOpen = false;
  @Input() reviewTypeId: number = 1;
  @Input() typeOfReview: string = '';
  @Input() issueStatusMap: Record<string, string> = {};

  get isGoLive(): boolean { return this.reviewTypeId === 2; }

  private get defaultReportStatus(): string {
    if (!this.isGoLive) return '';
    return this.typeOfReview === 'post-only' ? 'Draft-Not Published' : 'Pre-Go Live';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      document.body.style.overflow = this.isOpen ? 'hidden' : '';
      if (this.isOpen && !this._editMode) {
        this.form.reportStatus = this.defaultReportStatus;
      }
      if (this.isOpen) {
        this.loadSupervisorEids();
      }
    }
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    if (this._pickerResizeHandler) {
      window.removeEventListener('resize', this._pickerResizeHandler);
      this._pickerResizeHandler = null;
    }
    this._activePicker = null;
  }

  get isFormDirty(): boolean {
    return !!(
      this.form.issueTitle.trim() ||
      this.form.issueDescription.trim() ||
      this.form.reviewObjective.trim() ||
      this.form.recommendation.trim() ||
      (this.form.rcReviewer !== this._rcReviewerFromReview) ||
      (this.form.rcSupervisor !== this._rcSupervisorFromReview) ||
      (this.form.rcManager !== this._rcManagerFromReview) ||
      this.form.identificationDate ||
      this.form.subscribers.length ||
      this.form.rcCommentsForIssue.trim() ||
      this.form.actionRequiredBy ||
      this.form.issueRating ||
      this.form.locationsImpacted.length ||
      this.form.broadCategory ||
      this.form.detailedCategory ||
      this.form.riskStatement ||
      this.form.repLetterConflict ||
      this.form.serviceLines.length ||
      (this.form.reportStatus !== this.defaultReportStatus) ||
      this.attachedFiles.length
    );
  }

  markDirty(): void { /* no-op: isFormDirty is computed from field values */ }

  get isActionPlanFormDirty(): boolean {
    if (this.newActionPlans.length > 1) return true;
    return this.newActionPlans.some((ap, i) =>
      ap.description.trim() ||
      ap.closedDate ||
      ap.internalDependencies.length > 0 ||
      ap.targetDate ||
      ap.owners.length > 0 ||
      ap.revisedTargetDate ||
      ap.rcComments.trim() ||
      ap.clientDependency ||
      ap.redClientInformedDate ||
      ap.targetDateToInformRedClient ||
      ap.legacyApNumber.trim() ||
      (ap.status && !(this.form.issueRating === 'Blue' && ap.status === 'Closed-Blue Not Tracked')) ||
      (this.apFilesByIndex[i]?.length > 0)
    );
  }

  validateRcCommentsForIssue(): void {
    const label = this.isGoLive ? 'R&C Comments' : 'R&C Comments for Issue';
    if (!this.form.rcCommentsForIssue.trim()) {
      this.formErrors['rcCommentsForIssue'] = `Fill the '${label}'`;
    } else {
      delete this.formErrors['rcCommentsForIssue'];
    }
  }

  isClientInformedDateEnabled(ap: ActionPlanForm): boolean {
    return this.isGoLive && this.form.issueRating === 'Red' && ap.clientDependency === 'Yes';
  }

  @Input() set riskCategories(groups: any[]) {
    if (!Array.isArray(groups)) return;
    const rows: RiskCategoryRow[] = [];
    for (const l1 of groups) {
      const broadCategory = String(l1?.BroadIssueCategoryL1 ?? '').trim();
      if (!broadCategory) continue;
      const l2List = Array.isArray(l1?.DetailedCategories) ? l1.DetailedCategories : [];
      for (const l2 of l2List) {
        const detailedCategory = String(l2?.DetailedIssueCategoryL2 ?? '').trim();
        const l3List = Array.isArray(l2?.RiskStatements) ? l2.RiskStatements : [];
        for (const l3 of l3List) {
          const riskStatement = String(l3?.RiskStatementL3 ?? '').trim();
          rows.push({ broadCategory, detailedCategory, riskStatement });
        }
      }
    }
    this.riskCategoryData = rows;
  }

  @Input() set serviceLines(lines: string[]) {
    if (lines && lines.length) this.serviceLineOptions = lines;
  }

  @Input() set locations(locs: string[]) {
    if (locs && locs.length) this.locationOptions = locs;
  }

  @Input() set repLetterQuestions(questions: string[]) {
    if (questions && questions.length) this.repLetterQuestionsOptions = questions;
  }

  private _rcReviewerFromReview = '';
  private _rcSupervisorFromReview = '';
  private _rcManagerFromReview = '';
  private _rcSecondaryReviewerFromReview = '';
  private _rcSecondarySupervisorFromReview = '';
  private _rcSecondaryManagerFromReview = '';

  secondaryRcReviewer = '';
  secondaryRcSupervisor = '';
  secondaryRcManager = '';

  @Input() set rcReviewerFromReview(val: string) {
    this._rcReviewerFromReview = val;
    if (!this._editMode) this.form.rcReviewer = val;
  }

  @Input() set rcSupervisorFromReview(val: string) {
    this._rcSupervisorFromReview = val;
    if (!this._editMode) this.form.rcSupervisor = val;
  }

  @Input() set rcManagerFromReview(val: string) {
    this._rcManagerFromReview = val;
    if (!this._editMode) this.form.rcManager = val;
  }

  @Input() set rcSecondaryReviewerFromReview(val: string) {
    this._rcSecondaryReviewerFromReview = val;
    this.secondaryRcReviewer = val;
  }

  @Input() set rcSecondarySupervisorFromReview(val: string) {
    this._rcSecondarySupervisorFromReview = val;
    this.secondaryRcSupervisor = val;
  }

  @Input() set rcSecondaryManagerFromReview(val: string) {
    this._rcSecondaryManagerFromReview = val;
    this.secondaryRcManager = val;
  }

  // Backing values for @Input-derived fields, re-applied whenever the form is reset.
  private issueIdValue: string | null = null;
  private actionPlanIdValue: string | null = null;
  private financialYearValue: string | null = null;
  private quarterValue: string | null = null;
  private _prevFinancialYear: string | null = null;
  private _prevQuarter: string | null = null;

  /** Action Plan ID to display, fetched by the parent from GetLastInsertedActionPlanByReviewID. */
  @Input() set actionPlanId(val: string | null) {
    this.actionPlanIdValue = val;
  }

  /** ReviewID is still received (template binds it) but no longer drives the Issue ID. */
  @Input() reviewId: number | null = null;

  /** Issue ID to display, fetched by the parent from GetLastInsertedIssueByReviewID. */
  @Input() set issueId(val: string | null) {
    this.issueIdValue = val;
    if (val && !this._editMode) this.form.issueId = val;
  }

  @Input() set financialYear(val: string | null) {
    this.financialYearValue = val;
    if (val) this.form.financialYear = val;
  }

  @Input() set quarter(val: string | null) {
    this.quarterValue = val;
    if (val) this.form.quarter = val;
  }

  @Output() closed = new EventEmitter<void>();
  @Output() saveDraft = new EventEmitter<{ step: 1 | 2; form: AddIssueForm; files: File[]; actionPlans: ActionPlanRow[]; newActionPlans: ActionPlanForm[]; newActionPlanFiles: File[][]; deletedFilePaths: string[] }>();
  @Output() sendForApproval = new EventEmitter<{ step: 1 | 2; form: AddIssueForm; files: File[]; actionPlans: ActionPlanRow[]; newActionPlans: ActionPlanForm[]; newActionPlanFiles: File[][]; deletedFilePaths: string[] }>();

  currentStep: 1 | 2 = 1;
  actionPlanSearch = '';

  // ── Existing files (edit mode) ──
  existingIssueDocuments: ExistingFile[] = [];
  private removedExistingDocPaths = new Set<string>();

  @Input() set existingFiles(docs: ExistingFile[] | null) {
    this.existingIssueDocuments = Array.isArray(docs) ? docs : [];
    this.removedExistingDocPaths = new Set();
  }

  removeExistingDoc(doc: ExistingFile) {
    this.existingIssueDocuments = this.existingIssueDocuments.filter(
      (d) => d.StorageFilePath !== doc.StorageFilePath,
    );
    this.removedExistingDocPaths.add(doc.StorageFilePath);
    if (this._editMode) {
      const issueId = Number(String(this.form.issueId || '').replace(/^[A-Za-z]+-/i, '').trim());
      const eid = this.userRoleService.enterpriseId || (this.userRoleService.email ?? '').split('@')[0];
      this.reviewerService.deleteIssueDocument(issueId, eid).subscribe({ error: () => {} });
    }
  }

  downloadExistingDoc(doc: ExistingFile): void {
    if (doc.fileContent) {
      const link = document.createElement('a');
      link.href = 'data:application/octet-stream;base64,' + doc.fileContent;
      link.download = doc.FileName;
      link.click();
      return;
    }
    const filePath = `${this.reviewId}/${this.form.issueId}/${doc.FileName}`;
    this.reviewerService.downloadFile(filePath).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.FileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: () => {},
    });
  }

  downloadAttachedFile(file: File): void {
    const reader = new FileReader();
    reader.onload = () => {
      const link = document.createElement('a');
      link.href = reader.result as string;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    reader.readAsDataURL(file);
  }

  // ── Create New Action Plan (Step 2 inline form) ──
  showCreateActionPlan = false;

  // Array of new action plan forms (one per "+ Add Another Action Plan" click)
  newActionPlans: ActionPlanForm[] = [];
  apExpandedIndex: number | null = null;      // which accordion is currently open
  apErrorsByIndex: Record<string, string>[] = [];   // per-form validation errors
  apOwnerPickerValues: string[] = [];               // per-form owner search input
  apFilesByIndex: File[][] = [];                    // per-form uploaded files
  apUploadErrorsByIndex: string[] = [];             // per-form file upload error message

  actionPlanStatusOptions = [...ACTION_PLAN_STATUS_OPTIONS];

  // Option lists for the searchable multi-selects (populated from API via parent @Input)
  internalDependencyOptions: string[] = [];

  @Input() set internalDependencies(deps: string[]) {
    if (deps && deps.length) this.internalDependencyOptions = deps;
  }

  internalDependencySearch = '';
  internalDependencyDropdownOpen = false;

  // Existing action plans for the current review, loaded from GetActionPlans (parent passes them in).
  actionPlanRows: ActionPlanRow[] = [];

  /** Map the GetActionPlans API response to the Step 2 list rows. */
  @Input() set actionPlans(list: any[]) {
    if (!Array.isArray(list)) { this.actionPlanRows = []; return; }
    this.actionPlanRows = list.map((a: any) => {
      const rawId = String(
        a.ActionPlanID ?? a.ActionPlanId ?? a.ID ?? a.Id ?? a.ActionID ?? '',
      ).trim();
      const id = rawId ? (rawId.startsWith('AP-') ? rawId : 'AP-' + rawId) : '';
      // TargetDate is a full ISO datetime (2025-11-27T00:00:00.000Z) — take the date part only.
      const target = String(a.AP_TargetDate ?? a.TargetDateForActionPlanRemediationAction ?? a.TargetDate ?? a.TargetDateForActionPlan ?? a.ActionPlanTargetDate ?? '').split('T')[0];
      // Owner is the full email from OwnerEmail.
      const owner = String(a.AP_OwnerEmail || a.OwnerEmail || a.Owner || a.OwnerName || a.ActionOwner || a.Owners || '');
      return {
        id,
        description: a.AP_Description || a.ActionPlanDescription || a.Description || a.ActionDescription || '',
        owner,
        // formatDate converts YYYY-MM-DD → DD/MM/YYYY; fall back to the raw value otherwise.
        targetDate: this.formatDate(target) || target,
        status: a.AP_Status || a.Status || a.ActionPlanStatus || '',
        linkedIssues: (a.LinkedIssueIDs ?? '').split(',').map((id: string) => id.trim()).filter(Boolean).map((id: string) => 'I-' + id).join(', ') || (a.LinkedIssueNames ?? a.LinkedIssues ?? a.LinkedIssue ?? ''),
        linked: false,
        closedDate: String(a.AP_ClosedDate ?? a.ClosedDateForActionPlan ?? a.ClosedDate ?? '').split('T')[0],
        rcComments: a.AP_RCComments ?? a.RCComments ?? '',
      } as ActionPlanRow;
    });
    this.applyPreLinkedAps();
  }

  // ── Edit mode ──
  _editMode = false;
  private _preLinkedApIds = new Set<string>();
  private _userUncheckedApIds = new Set<string>();

  onApLinkToggle(row: ActionPlanRow, checked: boolean): void {
    if (!checked) {
      this._userUncheckedApIds.add(row.id);
    } else {
      this._userUncheckedApIds.delete(row.id);
    }
  }

  /** Converts a date from display format (DD-MMM-YYYY or ISO) to YYYY-MM-DD for date pickers. */
  private toIsoDate(dateStr: string): string {
    if (!dateStr) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      // Use local date methods to avoid UTC-vs-local timezone offset shifting the date by 1 day.
      const d = new Date(dateStr);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    const parts = dateStr.split('-');
    if (parts.length !== 3) return '';
    const monthMap: Record<string, string> = {
      Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
      Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
    };
    const month = monthMap[parts[1]];
    if (!month) return '';
    return `${parts[2]}-${month}-${parts[0].padStart(2, '0')}`;
  }

  /** Converts stored FinancialYearID ("2026") back to display format ("FY26"). */
  private parseFinancialYear(raw: string): string {
    if (!raw) return '';
    if (/^FY\d+$/i.test(raw)) return raw.toUpperCase();
    if (/^\d{4}$/.test(raw)) return 'FY' + raw.slice(2);
    return raw;
  }

  /** Converts an ISO datetime or DD-MMM-YYYY string to DD-MMM-YYYY display format. */
  private toDisplayDateSafe(raw: string): string {
    if (!raw) return '';
    if (/^\d{2}-[A-Za-z]{3}-\d{4}$/.test(raw)) return raw;
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      // Use local date methods to avoid UTC-vs-local timezone offset shifting the date by 1 day.
      const d = new Date(raw);
      const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
    }
    return raw;
  }

  /** Pre-check linked APs when in edit mode (called after actionPlanRows is rebuilt). */
  private applyPreLinkedAps(): void {
    if (!this._editMode) return; // new-issue flow has no pre-linked APs to restore
    const hasCache = this._preLinkedApIds.size > 0;
    if (!hasCache) return;
    // Trust only _preLinkedApIds — the AP's own linkedIssues field cannot be relied on
    // because the backend stored procedure incorrectly links all APs to every new issue.
    this.actionPlanRows.forEach((r) => {
      if (this._userUncheckedApIds.has(r.id)) return; // user explicitly unchecked — never restore
      if (this._preLinkedApIds.has(r.id)) {
        if (!r.status.trim()) return; // blank-status APs are filtered out of the grid — don't pre-link
        r.linked = true;
        if (!r.linkedIssues && this.form.issueId) r.linkedIssues = this.form.issueId;
      }
    });
  }

  /** Receives the issue to edit; pre-populates all Step 1 form fields. */
  @Input() set editIssueData(issue: any | null) {
    console.log('[editIssueData setter] IssueID:', issue?.IssueID, '| Subscribers:', issue?.Subscribers);
    if (!issue) {
      this._editMode = false;
      this._preLinkedApIds = new Set();
      return;
    }
    this._editMode = true;
    this.currentStep = 1;

    const rawIssueId = String(issue.IssueID ?? '');
    this.form.issueId = rawIssueId && !/^[A-Za-z]/.test(rawIssueId) ? `I-${rawIssueId}` : rawIssueId;
    this.form.issueStatus      = issue.IssueStatus ?? '';
    this.form.issueTitle       = issue.IssueTitle ?? '';
    this.form.issueDescription = issue.IssueDescription ?? '';
    // Use issue-specific reviewer IDs (EIDs) for people-picker pre-population.
    // IssueRCReviewerID comes from i.RCReviewerID; fall back to display name.
    this.form.rcReviewer   = issue.IssueRCReviewerID  || issue.IssueRCReviewer  || this._rcReviewerFromReview  || '';
    this.form.rcSupervisor = issue.IssueRCSupervisorID || issue.IssueRCSupervisor || this._rcSupervisorFromReview || '';
    this.form.rcManager    = issue.IssueRCManagerID    || issue.IssueRCManager    || this._rcManagerFromReview   || '';
    this.form.identificationDate = this.toIsoDate(issue.IdentificationDate ?? '');
    this.form.rcCommentsForIssue = issue.RCComments ?? issue.RCCommentsForIssue ?? '';
    this.form.actionRequiredBy   = issue.ActionRequiredBy ?? '';
    this.form.issueRating        = issue.IssueRating ?? '';
    this.form.reviewObjective    = issue.ReviewObjective ?? '';
    this.form.recommendation     = issue.Recommendation ?? '';
    this.form.broadCategory      = issue.BroadIssueCategoryL1 ?? issue.BroadCategory ?? '';
    this.form.detailedCategory   = issue.DetailedIssueCategoryL2 ?? issue.DetailedCategory ?? '';
    this.form.riskStatement      = issue.RiskStatementL3 ?? issue.RiskStatement ?? '';
    const rlc = issue.RepLetterConflict;
    this.form.repLetterConflict =
      rlc === true  || rlc === 'true'  || rlc === 'Yes'                   ? 'Yes'            :
      rlc === 'Not Applicable' || rlc === 'NotApplicable' || rlc === 'NA' ? 'Not Applicable' :
      rlc === false || rlc === 'false' || rlc === 'No'                    ? 'No'             : '';
    // Use issue-specific quarter and financial year (not the review-level ones).
    this.form.quarter       = issue.IssueRepLetterQuarter || this.quarterValue || 'Q1';
    this.form.financialYear = this.parseFinancialYear(issue.IssueFinancialYear || '') || this.financialYearValue || 'FY26';
    // IssueCloseDueDate was saved as MaxTargetDate in the API.
    this.form.issueCloseDueDate = this.toDisplayDateSafe(issue.IssueMaxTargetDate || issue.IssueCloseDueDate || '');
    // ReportStatus was saved as ReportStatus in the API, mapped here as IssueReportStatus.
    this.form.reportStatus = issue.IssueReportStatus || issue.ReportStatus || '';
    this.form.issueFromGoLive  = issue.IssueFromGoLive ?? '';
    this.form.legacyIssueNumber = issue.LegacyIssueNumber ?? '';

    // Arrays — may arrive as comma-separated strings or real arrays
    const toArr = (val: any) =>
      Array.isArray(val) ? val : (val ? String(val).split(',').map((s: string) => s.trim()).filter(Boolean) : []);

    // Normalise to EIDs — strip @domain. API returns Subscribers as [{IssueSubscriber, ...}] objects.
    const subscribersRaw = issue.Subscribers ?? issue.SubscriberNames ?? issue.NotifyEmails ?? issue.Notify ?? '';
    const subscribersArr: string[] = Array.isArray(subscribersRaw)
      ? subscribersRaw.map((s: any) => typeof s === 'string' ? s : (s.IssueSubscriber ?? s.EnterpriseId ?? s.Email ?? '')).filter(Boolean)
      : toArr(subscribersRaw);
    this.form.subscribers = subscribersArr.map((s: string) =>
      s.includes('@') ? s.split('@')[0] : s.trim()
    ).filter(Boolean);
    console.log('[editIssueData setter] form.subscribers after set:', this.form.subscribers);
    this.form.locationsImpacted              = toArr(issue.LocationsImpacted ?? issue.LocationNames);
    this.form.serviceLines                   = toArr(issue.ServiceLine ?? issue.ServiceLines);
    // 'N/A' is the placeholder saved when no question was chosen; treat it as empty so validation still applies.
    this.form.repLetterQuestionsWithConflict = toArr(issue.RepLetterQuestionsWithConflict)
      .filter((q: string) => q.toUpperCase() !== 'N/A');

    // Pre-check linked APs — handle string/number/array/object-array shapes.
    const rawAps = issue.LinkedActionPlan ?? issue.LinkedActionPlans;
    const apArr: any[] = Array.isArray(rawAps) ? rawAps : (rawAps ? String(rawAps).split(',').map((s: string) => s.trim()).filter(Boolean) : []);
    this._preLinkedApIds = new Set(
      apArr.map((item: any) => {
        let rawId: string;
        if (typeof item === 'string' || typeof item === 'number') rawId = String(item).trim();
        else rawId = String(item.ActionPlanID ?? item.ActionPlanId ?? item.ID ?? item.Id ?? '').trim();
        return rawId ? (rawId.startsWith('AP-') ? rawId : 'AP-' + rawId) : '';
      }).filter(Boolean)
    );
    console.log('[editIssueData] LinkedActionPlan raw:', rawAps, '| _preLinkedApIds:', [...this._preLinkedApIds]);
    this.applyPreLinkedAps();
  }

  get filteredActionPlanRows(): ActionPlanRow[] {
    // Normalize status for case-insensitive comparison; also treat '&' same as 'and'.
    const normalize = (s: string) => (s ?? '').toLowerCase().trim().replace(/\s*&\s*/g, ' and ');

    const BLUE_ONLY = normalize('Closed-Blue Not Tracked');
    const ALLOWED_FOR_NON_BLUE = new Set([normalize('Open'), normalize('Open-Ageing & Static'), normalize('Closed')]);
    const rating = this.form.issueRating;

    let rows = this.actionPlanRows;
    if (rating === 'Blue') {
      rows = rows.filter((r) => normalize(r.status) === BLUE_ONLY);
    } else if (rating === 'Red' || rating === 'Yellow' || rating === 'Green' || rating === 'Not Rated') {
      rows = rows.filter((r) => ALLOWED_FOR_NON_BLUE.has(normalize(r.status)));
    }

    const t = this.actionPlanSearch.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(
      (r) =>
        r.id.toLowerCase().includes(t) ||
        r.description.toLowerCase().includes(t) ||
        r.owner.toLowerCase().includes(t) ||
        r.targetDate.toLowerCase().includes(t) ||
        r.status.toLowerCase().includes(t) ||
        r.linkedIssues.toLowerCase().includes(t),
    );
  }

  get hasLinkedActionPlan(): boolean {
    return this.actionPlanRows.some(
      (r) => r.linked && r.status.trim() && r.status.trim().toLowerCase() !== 'inactive'
    );
  }

  /** True when a linked existing AP is missing a field that is mandatory on the AP form. */
  private isExistingApIncomplete(r: ActionPlanRow): boolean {
    const status = (r.status ?? '').trim().toLowerCase();
    if (!(r.description ?? '').trim() || !status || !(r.owner ?? '').trim() || !(r.rcComments ?? '').trim()) return true;
    if (this.isClosedDateRequired({ status }) && !(r.closedDate ?? '').trim()) return true;
    // Target Date is not required for Closed / Closed-Blue Not Tracked APs.
    return !this.isClosedDateRequired({ status }) && !(r.targetDate ?? '').trim();
  }

  /** IDs of linked existing APs that have mandatory fields missing. */
  get incompleteLinkedApIds(): string[] {
    return this.actionPlanRows.filter((r) => r.linked && this.isExistingApIncomplete(r)).map((r) => r.id);
  }

  get incompleteApMessage(): string {
    const ids = this.incompleteLinkedApIds;
    if (!ids.length) return '';
    return `${ids.join(', ')} ${ids.length > 1 ? 'have' : 'has'} mandatory fields missing. Please Update the Action Plan(s) and submit for approval`;
  }

  get linkedActionPlans(): ActionPlanRow[] {
    const visiblyLinked = this.actionPlanRows.filter((r) => r.linked);
    // Preserve previously-linked blank-status APs (hidden from grid, not pre-checked)
    // so they are not dropped from the save payload when the user links a new AP.
    const blankStatusPreserved = this.actionPlanRows.filter(
      (r) => !r.linked && !r.status.trim() && this._preLinkedApIds.has(r.id) && !this._userUncheckedApIds.has(r.id)
    );
    return [...visiblyLinked, ...blankStatusPreserved];
  }

  /**
   * Issue Close Due Date = furthest Target Date among all linked Action Plans (any status),
   * including APs being created in this session.
   * Empty when no linked AP / AP does not has a target date
   */
  get computedIssueCloseDueDate(): string {
    const dates = [
      ...this.linkedActionPlans.map((r) => r.targetDate),
      ...(this.showCreateActionPlan ? this.newActionPlans.map((ap) => ap.targetDate) : []),
    ]
      .map((d) => this.toIsoDate(this.formatDate(d)))
      .filter(Boolean);
    if (!dates.length) return '';
    return this.formatDate(dates.reduce((a, b) => (b > a ? b : a)));
  }

  get today(): string {
    return new Date().toISOString().split('T')[0];
  }

  // Identification Date is held internally as ISO (yyyy-MM-dd) for the picker + validation,
  // but displayed to the user as DD-MMM-YYYY.
  get identificationDateDisplay(): string {
    const iso = this.form.identificationDate;
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return '';
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d.toString().padStart(2, '0')}-${MONTHS[m - 1]}-${y}`;
  }

  openIdentificationDatePicker(input: HTMLInputElement): void {
    input.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'center' });
    const picker = input as HTMLInputElement & { showPicker?: () => void };
    this._activePicker = input;
    if (!this._pickerResizeHandler) {
      this._pickerResizeHandler = () => {
        if (this._activePicker) {
          const p = this._activePicker as HTMLInputElement & { showPicker?: () => void };
          p.blur();
          setTimeout(() => p.showPicker?.(), 0);
        }
      };
      window.addEventListener('resize', this._pickerResizeHandler);
    }
    if (typeof picker.showPicker === 'function') picker.showPicker();
    else input.focus();
  }

  form: AddIssueForm = createDefaultAddIssueForm();

  // ── dropdown option lists (sourced from constants) ──
  locationOptions = [...LOCATION_OPTIONS];
  actionRequiredByOptions = [...ACTION_REQUIRED_BY_OPTIONS];
  actionRequiredByDropdownOpen = false;
  repLetterConflictOptions = [...REP_LETTER_CONFLICT_OPTIONS];
  repLetterConflictDropdownOpen = false;

  // ── risk categories (L1 → L2 → L3), sourced from GetRiskCategories API (provided by parent) ──
  private riskCategoryData: RiskCategoryRow[] = [];

  private distinct(values: string[]): string[] {
    return [...new Set(values.filter((v) => v.length > 0))];
  }

  get broadCategoryOptions(): string[] {
    return this.distinct(this.riskCategoryData.map((r) => r.broadCategory));
  }

  get detailedCategoryOptions(): string[] {
    return this.distinct(
      this.riskCategoryData
        .filter((r) => r.broadCategory === this.form.broadCategory)
        .map((r) => r.detailedCategory),
    );
  }

  get riskStatementOptions(): string[] {
    return this.distinct(
      this.riskCategoryData
        .filter(
          (r) =>
            r.broadCategory === this.form.broadCategory &&
            r.detailedCategory === this.form.detailedCategory,
        )
        .map((r) => r.riskStatement),
    );
  }

  onBroadCategoryChange(): void {
    this.form.detailedCategory = '';
    this.form.riskStatement = '';
    this.detailedCategoryDropdownOpen = false;
    this.riskStatementDropdownOpen = false;
    this.validateField('broadCategory');
    delete this.formErrors['detailedCategory'];
    delete this.formErrors['riskStatement'];
  }

  onDetailedCategoryChange(): void {
    this.form.riskStatement = '';
    this.riskStatementDropdownOpen = false;
    this.validateField('detailedCategory');
    delete this.formErrors['riskStatement'];
  }
  repLetterQuestionsOptions = [...REP_LETTER_QUESTIONS_OPTIONS];

  repLetterQuestionsSearch = '';
  repLetterQuestionsDropdownOpen = false;

  get filteredRepLetterQuestions(): string[] {
    const q = this.repLetterQuestionsSearch.toLowerCase();
    return this.repLetterQuestionsOptions.filter(
      (o) => !this.form.repLetterQuestionsWithConflict.includes(o) && o.toLowerCase().includes(q)
    );
  }

  addRepLetterQuestion(question: string) {
    if (question && !this.form.repLetterQuestionsWithConflict.includes(question)) {
      let updated = [...this.form.repLetterQuestionsWithConflict, question];
      if (question !== 'N/A') {
        updated = updated.filter((q) => q !== 'N/A');
      } else {
        updated = ['N/A'];
      }
      this.form.repLetterQuestionsWithConflict = updated;
      delete this.formErrors['repLetterQuestionsWithConflict'];
      this.markDirty();
    }
    this.repLetterQuestionsSearch = '';
    this.repLetterQuestionsDropdownOpen = false;
  }

  removeRepLetterQuestion(question: string) {
    this.form.repLetterQuestionsWithConflict = this.form.repLetterQuestionsWithConflict.filter((q) => q !== question);
    this.markDirty();
  }

  onRepLetterConflictChange() {
    this.validateField('repLetterConflict');
    if (this.form.repLetterConflict === 'Yes') {
      this._prevFinancialYear = this.form.financialYear;
      this._prevQuarter = this.form.quarter;
    } else {
      delete this.formErrors['repLetterQuestionsWithConflict'];
      delete this.formErrors['financialYear'];
      delete this.formErrors['quarter'];
      this.form.repLetterQuestionsWithConflict = [];
      this.repLetterQuestionsSearch = '';
      if (this._prevFinancialYear !== null) this.form.financialYear = this._prevFinancialYear;
      if (this._prevQuarter !== null) this.form.quarter = this._prevQuarter;
    }
  }

  removeRepLetterQuestionWithValidation(question: string) {
    if (this.form.repLetterConflict !== 'Yes') return;
    this.removeRepLetterQuestion(question);
    if (this.form.repLetterConflict === 'Yes') {
      if (this.form.repLetterQuestionsWithConflict.length === 0) {
        this.formErrors['repLetterQuestionsWithConflict'] = `Select the '${this.isGoLive ? 'Pre Go-Live/Mob RepLetter Questions with Conflict' : 'RepLetter Questions with Conflict'}'`;
      } else {
        delete this.formErrors['repLetterQuestionsWithConflict'];
      }
    }
  }

  closeRepLetterQuestionsDropdown() {
    setTimeout(() => (this.repLetterQuestionsDropdownOpen = false), 150);
  }
  financialYearOptions = [...FINANCIAL_YEAR_OPTIONS];
  quarterOptions = [...QUARTER_OPTIONS];
  issueRatingOptions = [...ISSUE_RATING_OPTIONS];
  issueRatingDropdownOpen = false;
  serviceLineOptions = [...SERVICE_LINE_OPTIONS];
  reportStatusOptions = [...REPORT_STATUS_OPTIONS];

  // ── location multi-select ──
  locationSearchTerm = '';
  locationDropdownOpen = false;

  get filteredLocationOptions(): string[] {
    const q = this.locationSearchTerm.toLowerCase();
    return this.locationOptions.filter(
      (o) => !this.form.locationsImpacted.includes(o) && o.toLowerCase().includes(q)
    );
  }

  selectLocation(loc: string) {
    if (!this.form.locationsImpacted.includes(loc)) {
      this.form.locationsImpacted = [...this.form.locationsImpacted, loc];
      delete this.formErrors['locationsImpacted'];
      this.markDirty();
    }
    this.locationSearchTerm = '';
    this.locationDropdownOpen = false;
  }

  closeLocationDropdown() {
    setTimeout(() => (this.locationDropdownOpen = false), 150);
  }

  // ── service line multi-select ──
  serviceLineDropdownOpen = false;

  get availableServiceLines(): string[] {
    return this.serviceLineOptions.filter((o) => !this.form.serviceLines.includes(o));
  }

  selectServiceLine(sl: string) {
    if (!this.form.serviceLines.includes(sl)) {
      this.form.serviceLines = [...this.form.serviceLines, sl];
      delete this.formErrors['serviceLines'];
      this.markDirty();
    }
  }

  closeServiceLineDropdown() {
    setTimeout(() => (this.serviceLineDropdownOpen = false), 150);
  }

  // ── report status custom dropdown ──
  reportStatusDropdownOpen = false;

  issueFromGoLiveOptions = [...ISSUE_FROM_GO_LIVE_OPTIONS];
  issueFromGoLiveDropdownOpen = false;

  selectIssueFromGoLive(opt: string) {
    this.form.issueFromGoLive = opt;
    this.issueFromGoLiveDropdownOpen = false;
    this.validateField('issueFromGoLive');
  }

  selectReportStatus(opt: string) {
    this.form.reportStatus = opt;
    this.reportStatusDropdownOpen = false;
    this.markDirty();
    this.validateField('reportStatus');
  }

  closeReportStatusDropdown() {
    setTimeout(() => (this.reportStatusDropdownOpen = false), 150);
  }

  broadCategoryDropdownOpen = false;
  detailedCategoryDropdownOpen = false;
  riskStatementDropdownOpen = false;

  toggleReportStatusDropdown(trigger: HTMLElement): void {
    this.reportStatusDropdownOpen = !this.reportStatusDropdownOpen;
    if (this.reportStatusDropdownOpen) {
      setTimeout(() => {
        const dropdown = trigger.closest('.select-wrapper')?.querySelector('.multiselect-dropdown') as HTMLElement;
        dropdown?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }

  toggleIssueRatingDropdown(trigger: HTMLElement): void {
    this.issueRatingDropdownOpen = !this.issueRatingDropdownOpen;
    if (this.issueRatingDropdownOpen) {
      setTimeout(() => {
        const dropdown = trigger.closest('.select-wrapper')?.querySelector('.multiselect-dropdown') as HTMLElement;
        dropdown?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }

  // ── navigation ──
  close() {
    this._editMode = false;
    this._preLinkedApIds = new Set();
    this.currentStep = 1;
    this.resetForm();
    this.closed.emit();
  }

  private resetForm() {
    this.form = createDefaultAddIssueForm();
    // re-apply values supplied by the parent via @Input
    if (this.issueIdValue) this.form.issueId = this.issueIdValue;
    if (this.financialYearValue) this.form.financialYear = this.financialYearValue;
    if (this.quarterValue) this.form.quarter = this.quarterValue;
    this.form.reportStatus = this.defaultReportStatus;
    if (this._rcReviewerFromReview) this.form.rcReviewer = this._rcReviewerFromReview;
    if (this._rcSupervisorFromReview) this.form.rcSupervisor = this._rcSupervisorFromReview;
    if (this._rcManagerFromReview) this.form.rcManager = this._rcManagerFromReview;
    this.secondaryRcReviewer  = this._rcSecondaryReviewerFromReview;
    this.secondaryRcSupervisor = this._rcSecondarySupervisorFromReview;
    this.secondaryRcManager   = this._rcSecondaryManagerFromReview;
    this.formErrors = {};
    this.locationSearchTerm = '';
    this.locationDropdownOpen = false;
    this.serviceLineDropdownOpen = false;
    this.repLetterQuestionsSearch = '';
    this.repLetterQuestionsDropdownOpen = false;
    this.uploadError = '';
    this.attachedFiles = [];
    this.existingIssueDocuments = [];
    this.removedExistingDocPaths = new Set();
    this.subscriberPickerValue = '';
    this.actionPlanRows.forEach((r) => (r.linked = false));
    this.showCreateActionPlan = false;
    this.internalDependencySearch = '';
    this.internalDependencyDropdownOpen = false;
    this.newActionPlans = [];
    this.apExpandedIndex = null;
    this.apErrorsByIndex = [];
    this.apOwnerPickerValues = [];
    this.apFilesByIndex = [];
    this.apUploadErrorsByIndex = [];
  }

  // ── Validation ──
  formErrors: Record<string, string> = {};

  private get validationRules(): Partial<Record<keyof AddIssueForm, { label: string; type: 'input' | 'select' }>> {
    return {
      issueTitle:          { label: 'Issue Title',                    type: 'input'  },
      issueDescription:    { label: 'Issue Description',              type: 'input'  },
      reviewObjective:     { label: 'Review Objective',               type: 'input'  },
      recommendation:      { label: 'Recommendation',                 type: 'input'  },
      identificationDate:  { label: 'Identification Date',            type: 'input'  },
      actionRequiredBy:    { label: 'Action Required By',             type: 'select' },
      issueRating:         { label: 'Issue Rating',                   type: 'select' },
      locationsImpacted:   { label: 'Location Reviewed and Impacted', type: 'select' },
      broadCategory:       { label: 'Broad Issue Category (L1)',      type: 'select' },
      detailedCategory:    { label: 'Detailed Issue Category (L2)',    type: 'select' },
      riskStatement:       { label: 'Risk Statement (L3)',             type: 'select' },
      repLetterConflict:   { label: this.isGoLive ? 'Pre Go-Live/Mob RepLetter Conflict' : 'RepLetter Conflict', type: 'select' },
      serviceLines:        { label: 'Accenture Operation Service Line', type: 'select' },
      ...(this.isGoLive ? { reportStatus: { label: 'Report Status', type: 'select' as const } } : {}),
      ...(this.isGoLive ? { issueFromGoLive: { label: 'Is this Issue from Go-Live?', type: 'select' as const } } : {}),
    };
  }

  validateField(fieldName: keyof AddIssueForm) {
    this.markDirty();
    const rule = this.validationRules[fieldName];
    if (!rule) return;
    const value = this.form[fieldName];
    const isEmpty = Array.isArray(value) ? value.length === 0 : !String(value ?? '').trim();
    if (isEmpty) {
      const action = rule.type === 'select' ? 'Select' : 'Fill';
      this.formErrors[fieldName as string] = `${action} the '${rule.label}'`;
    } else {
      delete this.formErrors[fieldName as string];
    }
  }

  validateForm(): boolean {
    this.formErrors = {};
    (Object.keys(this.validationRules) as (keyof AddIssueForm)[]).forEach(f => this.validateField(f));

    if (this.form.repLetterConflict === 'Yes') {
      const conditional: { key: keyof AddIssueForm; label: string; type: 'input' | 'select' }[] = [
        { key: 'repLetterQuestionsWithConflict', label: this.isGoLive ? 'Pre Go-Live/Mob RepLetter Questions with Conflict' : 'RepLetter Questions with Conflict', type: 'select' },
        { key: 'financialYear' as keyof AddIssueForm, label: 'Financial Year', type: 'select' as const },
        { key: 'quarter'       as keyof AddIssueForm, label: 'Quarter',        type: 'select' as const },
      ];
      conditional.forEach(({ key, label, type }) => {
        const val = this.form[key];
        const empty = Array.isArray(val) ? val.length === 0 : !String(val ?? '').trim();
        if (empty) this.formErrors[key as string] = `${type === 'select' ? 'Select' : 'Fill'} the '${label}'`;
      });
    }

    if (!this.form.rcCommentsForIssue.trim()) {
      const label = this.isGoLive ? 'R&C Comments' : 'R&C Comments for Issue';
      this.formErrors['rcCommentsForIssue'] = `Fill the '${label}'`;
    }

    return Object.keys(this.formErrors).length === 0;
  }

  scrollToFirstError() {
    const first = Object.keys(this.formErrors)[0];
    if (!first) return;
    const el = document.querySelector(`[data-field="${first}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  onCancel() {
    // close() already resets the form
    this.close();
  }

  goToStep2() {
    if (!this.validateForm()) { this.scrollToFirstError(); return; }
    this.applyPreLinkedAps();
    this.currentStep = 2;
  }

  backToStep1() {
    this.currentStep = 1;
    this.formErrors = {};
    this.showCreateActionPlan = false;
    this.newActionPlans = [];
    this.apExpandedIndex = null;
    this.apErrorsByIndex = [];
  }

  onSaveDraft() {
    if (this.subscriberPickerValue.trim()) {
      this.addSubscriber(this.subscriberPickerValue);
    }
    this.form.issueCloseDueDate = this.computedIssueCloseDueDate;
    this.saveDraft.emit({
      step: this.currentStep,
      form: this.form,
      files: this.attachedFiles,
      actionPlans: this.linkedActionPlans,
      newActionPlans: this.showCreateActionPlan ? [...this.newActionPlans] : [],
      newActionPlanFiles: this.showCreateActionPlan ? [...this.apFilesByIndex] : [],
      deletedFilePaths: [...this.removedExistingDocPaths],
    });
    this.close();
  }

  onSendForApproval() {
    if (this.incompleteLinkedApIds.length > 0) return;
    if (this.form.issueRating === 'Blue') {
      if (!this.validateForm()) { this.scrollToFirstError(); return; }
    }
    this.form.issueCloseDueDate = this.computedIssueCloseDueDate;
    if (this.showCreateActionPlan) {
      if (!this.validateAllActionPlans()) { this.scrollToFirstActionPlanError(); return; }
      this.sendForApproval.emit({
        step: this.currentStep,
        form: this.form,
        files: this.attachedFiles,
        actionPlans: this.linkedActionPlans,
        newActionPlans: [...this.newActionPlans],
        newActionPlanFiles: [...this.apFilesByIndex],
        deletedFilePaths: [...this.removedExistingDocPaths],
      });
      this.close();
      return;
    }
    this.sendForApproval.emit({
      step: this.currentStep,
      form: this.form,
      files: this.attachedFiles,
      actionPlans: this.linkedActionPlans,
      newActionPlans: [],
      newActionPlanFiles: [],
      deletedFilePaths: [...this.removedExistingDocPaths],
    });
    this.close();
  }

  onIssueRatingChange(): void {
    this.validateField('issueRating');
    this._userUncheckedApIds.clear();
    this.actionPlanRows.forEach(r => r.linked = false);
    this.newActionPlans = [];
    this.apErrorsByIndex = [];
    this.apOwnerPickerValues = [];
    this.apFilesByIndex = [];
    this.apUploadErrorsByIndex = [];
    this.apExpandedIndex = null;
    this.showCreateActionPlan = false;
  }

  onCreateNewActionPlan() {
    if (this.showCreateActionPlan) return; // already open, do nothing
    if (this.newActionPlans.length === 0) {
      const status = this.form.issueRating === 'Blue' ? 'Closed-Blue Not Tracked' : '';
      const firstAp = { ...createDefaultActionPlanForm(), id: this.actionPlanIdValue ?? '', status };
      this.newActionPlans = [firstAp];
      this.apErrorsByIndex = [{}];
      this.apOwnerPickerValues = [''];
      this.apFilesByIndex = [[]];
      this.apUploadErrorsByIndex = [''];
      this.apExpandedIndex = 0;
    }
    this.showCreateActionPlan = true;
  }

  onAddAnotherActionPlan() {
    const lastId = this.newActionPlans[this.newActionPlans.length - 1]?.id ?? '';
    const num = parseInt(lastId.replace(/^AP-/i, ''), 10);
    const nextId = isNaN(num) ? '' : `AP-${num + 1}`;
    const status = this.form.issueRating === 'Blue' ? 'Closed-Blue Not Tracked' : '';
    const nextAp = { ...createDefaultActionPlanForm(), id: nextId, status };
    const newIndex = this.newActionPlans.length;
    this.newActionPlans = [...this.newActionPlans, nextAp];
    this.apErrorsByIndex = [...this.apErrorsByIndex, {}];
    this.apOwnerPickerValues = [...this.apOwnerPickerValues, ''];
    this.apFilesByIndex = [...this.apFilesByIndex, []];
    this.apUploadErrorsByIndex = [...this.apUploadErrorsByIndex, ''];
    this.apExpandedIndex = newIndex;
  }

  removeNewActionPlan(index: number) {
    const remaining = this.newActionPlans.filter((_, i) => i !== index);
    this.newActionPlans = remaining;
    this.apErrorsByIndex = this.apErrorsByIndex.filter((_, i) => i !== index);
    this.apOwnerPickerValues = this.apOwnerPickerValues.filter((_, i) => i !== index);
    this.apFilesByIndex = this.apFilesByIndex.filter((_, i) => i !== index);
    this.apUploadErrorsByIndex = this.apUploadErrorsByIndex.filter((_, i) => i !== index);
    if (this.newActionPlans.length === 0) {
      this.showCreateActionPlan = false;
      this.apExpandedIndex = null;
    } else {
      this.apExpandedIndex = Math.min(index, this.newActionPlans.length - 1);
    }
  }

  toggleApAccordion(index: number) {
    this.apExpandedIndex = this.apExpandedIndex === index ? null : index;
  }

  backToActionPlanList() {
    this.showCreateActionPlan = false;
    this.internalDependencySearch = '';
    this.internalDependencyDropdownOpen = false;
  }

  formatDate(iso: string): string {
    return this.dateFormatSvc.format(iso);
  }

  // ── Internal Dependency (optional, searchable multi-select) ──
  getFilteredInternalDependencyOptions(index: number): string[] {
    const q = this.internalDependencySearch.toLowerCase();
    const form = this.newActionPlans[index];
    if (!form) return [];
    return this.internalDependencyOptions.filter(
      (o) => !form.internalDependencies.includes(o) && o.toLowerCase().includes(q),
    );
  }

  selectInternalDependency(index: number, dep: string): void {
    const form = this.newActionPlans[index];
    if (!form) return;
    if (!form.internalDependencies.includes(dep)) {
      form.internalDependencies = [...form.internalDependencies, dep];
    }
    this.internalDependencySearch = '';
    this.internalDependencyDropdownOpen = false;
  }

  removeInternalDependency(index: number, dep: string): void {
    const form = this.newActionPlans[index];
    if (!form) return;
    form.internalDependencies = form.internalDependencies.filter((d) => d !== dep);
  }

  closeInternalDependencyDropdown(): void {
    setTimeout(() => (this.internalDependencyDropdownOpen = false), 150);
  }

  // ── Owner for Action Plan ──
  addActionPlanOwner(index: number): void {
    const name = (this.apOwnerPickerValues[index] ?? '').trim();
    const form = this.newActionPlans[index];
    if (!form || !name) return;
    const eid = name.includes('@') ? name.split('@')[0] : name;
    const isEmailFormat = /^[^\s@]+@accenture\.com$/i.test(name);
    if (!isEmailFormat || (this.supervisorEids.size > 0 && !this.supervisorEids.has(eid.toLowerCase()))) {
      this.apErrorsByIndex[index] = { ...(this.apErrorsByIndex[index] ?? {}), owners: `"${eid}" is not a valid enterprise ID.` };
      return;
    }
    if (!form.owners.includes(name)) {
      form.owners = [...form.owners, name];
      this.validateActionPlanField(index, 'owners');
    }
    this.apOwnerPickerValues[index] = '';
  }

  removeActionPlanOwner(index: number, name: string): void {
    const form = this.newActionPlans[index];
    if (!form) return;
    form.owners = form.owners.filter((o) => o !== name);
    this.validateActionPlanField(index, 'owners');
  }

  // ── Action Plan validation ──
  private readonly actionPlanRules: Record<string, { label: string; type: 'input' | 'select'; error?: string }> = {
    description:       { label: 'Action Plan Description',                          type: 'input'  },
    status:            { label: 'Action Plan Status',                               type: 'select', error: 'Action Plan Status is required. Please select a status before submitting.' },
    closedDate:        { label: 'Closed Date for Action Plan',                      type: 'select' },
    targetDate:        { label: 'Target Date for Action Plan/Remediation Action',   type: 'select' },
    owners:            { label: 'Owner for Action Plan/Remediation Action',         type: 'select' },
    rcComments:        { label: 'R&C Comments for Action Plan',                     type: 'input'  },
  };

  /** Closed Date is mandatory only for Closed / Closed-Blue Not Tracked; optional otherwise. */
  isClosedDateRequired(ap: { status?: string } | undefined): boolean {
    const s = (ap?.status ?? '').trim().toLowerCase();
    return s === 'closed' || s === 'closed-blue not tracked';
  }

  validateActionPlanField(index: number, field: string): void {
    const rule = this.actionPlanRules[field];
    if (!rule) return;
    const form = this.newActionPlans[index];
    if (!form) return;
    const errors = { ...(this.apErrorsByIndex[index] ?? {}) };
    if (field === 'targetDate') {
      const status = (form.status || '').toLowerCase().trim();
      if (status === '' || status === 'closed' || status === 'closed-blue not tracked') {
        delete errors['targetDate'];
        this.apErrorsByIndex[index] = errors;
        return;
      }
    }
    const value = (form as unknown as Record<string, unknown>)[field];
    const isEmpty = Array.isArray(value) ? value.length === 0 : !String(value ?? '').trim();
    if (field === 'closedDate' && !this.isClosedDateRequired(form)) {
      delete errors[field];
    } else if (isEmpty) {
      errors[field] = rule.error ?? `${rule.type === 'select' ? 'Select' : 'Fill'} the '${rule.label}'`;
    } else {
      delete errors[field];
    }
    this.apErrorsByIndex[index] = errors;
  }

  validateClosedDateOnStatusChange(index: number): void {
    // Only re-validate once an error is showing or a date was entered; avoids flagging an untouched field.
    const hadError = !!this.apErrorsByIndex[index]?.['closedDate'];
    if (hadError || this.newActionPlans[index]?.closedDate) {
      this.validateActionPlanField(index, 'closedDate');
    }
  }

  validateActionPlan(index: number): boolean {
    this.apErrorsByIndex[index] = {};
    Object.keys(this.actionPlanRules).forEach((f) => this.validateActionPlanField(index, f));
    const ap = this.newActionPlans[index];
    if (ap && this.isClientInformedDateEnabled(ap) && !ap.redClientInformedDate) {
      this.apErrorsByIndex[index]['redClientInformedDate'] = "Select the 'Red-Client Related Observation-Client Informed Date'";
    }
    return Object.keys(this.apErrorsByIndex[index]).length === 0;
  }

  validateAllActionPlans(): boolean {
    return this.newActionPlans.every((_, i) => this.validateActionPlan(i));
  }

  scrollToFirstActionPlanError(): void {
    const firstInvalidIndex = this.newActionPlans.findIndex((_, i) => Object.keys(this.apErrorsByIndex[i] ?? {}).length > 0);
    if (firstInvalidIndex >= 0) this.apExpandedIndex = firstInvalidIndex;
  }

  // ── chip removal ──
  removeLocation(loc: string) {
    this.form.locationsImpacted = this.form.locationsImpacted.filter((l) => l !== loc);
  }

  removeLocationWithValidation(loc: string) {
    this.removeLocation(loc);
    this.validateField('locationsImpacted');
  }

  
  subscriberPickerValue = '';
  subscriberError = '';
  subscriberSuggestions: string[] = [];
  apOwnerSuggestions: string[][] = [];
  private supervisorEids = new Set<string>();

  private loadSupervisorEids(): void {
    this.supervisorEids.clear();
    this.reviewerService.getUsersInformation('').subscribe({
      next: (res: any) => {
        const list: any[] = Array.isArray(res) ? res : (res?.value ?? res?.recordset ?? []);
        list.forEach((u: any) => {
          const enterpriseRaw = u.EnterpriseId ?? u.enterpriseId ?? u.EID ?? u.eid ?? '';
          const emailRaw = u.Email ?? u.email ?? u.InternetMail ?? '';
          const enterpriseEid = (enterpriseRaw.includes('@') ? enterpriseRaw.split('@')[0] : enterpriseRaw).toLowerCase().trim();
          const emailEid = emailRaw.includes('@') ? emailRaw.split('@')[0].toLowerCase().trim() : '';
          if (enterpriseEid) this.supervisorEids.add(enterpriseEid);
          if (emailEid && emailEid !== enterpriseEid) this.supervisorEids.add(emailEid);
        });
      },
      error: () => {},
    });
  }

  addSubscriber(email: string) {
    if (!email) return;
    const trimmed = email.trim();
    const eid = trimmed.includes('@') ? trimmed.split('@')[0] : trimmed;
    if (!eid) return;
    if (this.supervisorEids.size > 0 && !this.supervisorEids.has(eid.toLowerCase())) {
      this.subscriberError = `"${eid}" EID entered is not available in AST tool please enter valid EID.`;
      return;
    }
    this.subscriberError = '';
    if (!this.form.subscribers.includes(trimmed)) {
      this.form.subscribers = [...this.form.subscribers, trimmed];
      this.markDirty();
    }
    setTimeout(() => (this.subscriberPickerValue = ''), 0);
  }

  removeSubscriber(name: string) {
    this.form.subscribers = this.form.subscribers.filter((s) => s !== name);
    this.markDirty();
  }

  onSubscriberInput(): void {
    this.subscriberSuggestions = [];
  }

  selectSubscriberSuggestion(eid: string): void {
    this.subscriberError = '';
    this.subscriberSuggestions = [];
    if (!this.form.subscribers.includes(eid)) {
      this.form.subscribers = [...this.form.subscribers, eid];
      this.markDirty();
    }
    this.subscriberPickerValue = '';
  }

  onSubscriberBlur(): void {
    setTimeout(() => { this.subscriberSuggestions = []; }, 200);
  }

  closeSubscriberDropdown(): void {
    if (this.subscriberPickerRef) {
      this.subscriberPickerRef.dropdownOpen = false;
      this.subscriberPickerRef.peopleData = [];
    }
  }

  closeOwnerDropdown(index: number): void {
    const picker = this.ownerPickerRefs?.toArray()[index];
    if (picker) {
      picker.dropdownOpen = false;
      picker.peopleData = [];
    }
  }

  onSubscriberPersonSelected(person: IPeopleDetails): void {
    this.addSubscriber(person.InternetMail ?? person.EnterpriseId ?? '');
  }

  onOwnerPersonSelected(index: number, person: IPeopleDetails): void {
    const email = person.InternetMail ?? person.EnterpriseId ?? '';
    if (!email) return;
    const form = this.newActionPlans[index];
    if (!form) return;
    if (!form.owners.includes(email)) {
      form.owners = [...form.owners, email];
      this.validateActionPlanField(index, 'owners');
    }
    this.apOwnerPickerValues[index] = '';
    if (this.apErrorsByIndex[index]) this.apErrorsByIndex[index]['owners'] = '';
  }

  onOwnerInput(index: number): void {
    this.apOwnerSuggestions[index] = [];
  }

  selectOwnerSuggestion(index: number, eid: string): void {
    const form = this.newActionPlans[index];
    if (!form) return;
    if (!form.owners.includes(eid)) {
      form.owners = [...form.owners, eid];
      this.validateActionPlanField(index, 'owners');
    }
    this.apOwnerPickerValues[index] = '';
    this.apOwnerSuggestions[index] = [];
    if (this.apErrorsByIndex[index]) this.apErrorsByIndex[index]['owners'] = '';
  }

  onOwnerBlur(index: number): void {
    setTimeout(() => { this.apOwnerSuggestions[index] = []; }, 200);
  }

  openActionPlanInNewTab(apId: string): void {
    const numericId = apId.replace(/^AP-/i, '').trim();
    const urlTree = this.router.createUrlTree(['/action-plan', numericId], {
      queryParams: { reviewId: this.reviewId, viewOnly: 'true', isGoLive: this.isGoLive ? '1' : '0' }
    });
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }

  parseLinkedIssues(value: string): string[] {
    if (!value) return [];
    return value.split(',').map((s: string) => s.trim()).filter(Boolean);
  }

  getIssueStatusTooltip(id: string): string {
    return this.issueStatusMap[id] || '';
  }

  linkedIssuesTooltip(value: string): string {
    if (!value) return '';
    return this.parseLinkedIssues(value).join('\n');
  }

  removeServiceLine(sl: string) {
    this.form.serviceLines = this.form.serviceLines.filter((s) => s !== sl);
  }

  removeServiceLineWithValidation(sl: string) {
    this.removeServiceLine(sl);
    this.validateField('serviceLines');
  }

  // ── file upload (multiple) ──
  uploadError = '';
  attachedFiles: File[] = [];

  private readonly allowedExtensions = [...ALLOWED_FILE_EXTENSIONS];
  private readonly maxFileSizeBytes = MAX_FILE_SIZE_BYTES;

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.uploadError = '';
    if (!input.files || input.files.length === 0) return;

    const supportedTypes = 'PDF, MSG, XLS/XLSX, DOC/DOCX, TXT, PPT/PPTX, JPG/JPEG/PNG, ZIP';
    const maxMb = this.maxFileSizeBytes / (1024 * 1024);
    const errors: string[] = [];

    for (const file of Array.from(input.files)) {
      const ext = '.' + file.name.split('.').pop()!.toLowerCase();
      if (!this.allowedExtensions.includes(ext)) {
        errors.push(`${file.name}: invalid file selected. Supported types: ${supportedTypes}.`);
        continue;
      }
      if (file.size === 0) {
        errors.push(`"${file.name}" is empty (0 MB). Please upload a valid file with content.`);
        continue;
      }
      if (file.size > this.maxFileSizeBytes) {
        errors.push(`${file.name} exceeds the maximum size of ${maxMb} MB.`);
        continue;
      }
      const duplicate = this.attachedFiles.some((f) => f.name === file.name && f.size === file.size);
      if (!duplicate) { this.attachedFiles = [...this.attachedFiles, file]; this.markDirty(); }
    }

    if (errors.length) this.uploadError = errors.join(' ');

    input.value = '';
  }

  removeAttachedFile(index: number) {
    this.attachedFiles = this.attachedFiles.filter((_, i) => i !== index);
  }

  // ── Action Plan file upload — per-AP (indexed) ──
  onActionPlanFilesSelected(apIndex: number, event: Event) {
    const input = event.target as HTMLInputElement;
    this.apUploadErrorsByIndex[apIndex] = '';
    if (!input.files || input.files.length === 0) return;

    const supportedTypes = 'PDF, MSG, XLS/XLSX, DOC/DOCX, TXT, PPT/PPTX, JPG/JPEG/PNG, ZIP';
    const maxMb = this.maxFileSizeBytes / (1024 * 1024);
    const errors: string[] = [];
    const currentFiles = [...(this.apFilesByIndex[apIndex] ?? [])];

    for (const file of Array.from(input.files)) {
      const ext = '.' + file.name.split('.').pop()!.toLowerCase();
      if (!this.allowedExtensions.includes(ext)) {
        errors.push(`${file.name}: invalid file selected. Supported types: ${supportedTypes}.`);
        continue;
      }
      if (file.size === 0) {
        errors.push(`"${file.name}" is empty (0 MB). Please upload a valid file with content.`);
        continue;
      }
      if (file.size > this.maxFileSizeBytes) {
        errors.push(`${file.name} exceeds the maximum size of ${maxMb} MB.`);
        continue;
      }
      if (!currentFiles.some((f) => f.name === file.name && f.size === file.size)) {
        currentFiles.push(file);
      }
    }
    this.apFilesByIndex[apIndex] = currentFiles;
    if (errors.length) this.apUploadErrorsByIndex[apIndex] = errors.join(' ');
    input.value = '';
  }

  removeActionPlanFile(apIndex: number, fileIndex: number) {
    this.apFilesByIndex[apIndex] = (this.apFilesByIndex[apIndex] ?? []).filter((_, i) => i !== fileIndex);
  }
}